<script setup lang="ts">
import router from '@/router';
import ModifyLinkService from '@/services/ModifyLinkService.js';
import SendedFormPopUp from '@/components/modify/SendedFormPopUp.vue';
import SendingFormPopUp from '@/components/modify/SendingFormPopUp.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useModifyLinkStore } from '@/stores/modifyLink/modifyLink.store';
import VersionChooser from '@/components/common/VersionChooser.vue';
import { EventVersionList } from 'interface/data/event/versions';
import { ProductType } from 'interface/data/productTypes';
import { useEventStore } from '@/stores/event/event.store';
import { useStateStore } from '@/stores/state.store';
import { Logger } from '@/middlewares/sentry';
import { useAuthStore } from '@/stores';
import { EventPatternToClientId } from 'patternApps/event';
import { DEFAULT_PATTERN_ID } from 'interface/link/event';
import { extractDateFromToken, extractInitialsFromToken } from 'common/date';

const toast = useToast();

const isGallery = ref<boolean | null>(true);
const isFullDate = ref<boolean | null>(true);
const isInitials = ref<boolean | null>(true);
const selectedPatternId = ref<number>(DEFAULT_PATTERN_ID);

const isFormSending = ref<boolean>(false);
const isFormSended = ref<boolean>(false);

const { linkData } = useModifyLinkStore();
const authStore = useAuthStore();
const stateStore = useStateStore();
const eventStore = useEventStore();

const eventDate = extractDateFromToken(linkData.bundleToken);
const initials = extractInitialsFromToken(linkData.bundleToken);

const setGallery = () => {
  if (isGallery.value) {
    stateStore.enableGallery();
  } else {
    stateStore.disableGallery();
  }
};

const setUnderTextData = () => {
  if (eventDate) {
    if (isFullDate.value) {
      eventStore.getLangAllLangData().underTextData = `${String(eventDate.getDate()).padStart(2, '0')}.${String(eventDate.getMonth() + 1).padStart(2, '0')}.${eventDate.getFullYear()}`;
    } else {
      eventStore.getLangAllLangData().underTextData = eventDate.getFullYear();
    }
  }
};

const setInitials = () => {
  if (initials) {
    if (isInitials.value) {
      eventStore.getLangAllLangData().header = `${initials?.herName} & ${initials?.hisName}`;
    } else {
      eventStore.getLangAllLangData().header = '';
    }
  }
};

watch(isGallery, () => {
  setGallery();
});

watch(isFullDate, () => {
  setUnderTextData();
});

watch(isInitials, () => {
  setInitials();
});

watch(
  () => stateStore.isGallery,
  (val) => {
    if (val !== isGallery.value) {
      setGallery();
    }
  },
);

watch(
  () => eventStore.getLangAllLangData().underTextData,
  (val) => {
    if (val !== isFullDate.value) {
      setUnderTextData();
    }
  },
);

watch(
  () => eventStore.getLangAllLangData().header,
  (val) => {
    if (val !== isInitials.value) {
      setInitials();
    }
  },
);

const normalIsGallery = computed({
  get() {
    return isGallery.value;
  },
  set() {
    isGallery.value = true;
  },
});

const normalIsFullDate = computed({
  get() {
    return isFullDate.value;
  },
  set() {
    isFullDate.value = true;
  },
});

const normalIsInitials = computed({
  get() {
    return isInitials.value;
  },
  set() {
    isInitials.value = true;
  },
});

const reverseIsGallery = computed({
  get() {
    return !isGallery.value;
  },
  set() {
    isGallery.value = false;
  },
});

const reverseIsFullDate = computed({
  get() {
    return !isFullDate.value;
  },
  set() {
    isFullDate.value = false;
  },
});

const reverseIsInitials = computed({
  get() {
    return !isInitials.value;
  },
  set() {
    isInitials.value = false;
  },
});

function moveToPreview() {
  router.push({ hash: `#preview`, query: { smooth: 'true' } });
}

async function changeSelectedPattern(id: number) {
  selectedPatternId.value = id;

  try {
    stateStore.resetData();
    await authStore.trySetupData(EventPatternToClientId[id]);
    await stateStore.trySetupData(EventPatternToClientId[id]);
    await eventStore.trySetupData();
  } catch (err) {
    Logger.error(err);
  }
}

async function submitEventForm() {
  isFormSending.value = true;

  let isError = false;
  const id = (router.currentRoute.value.params['id'] as string) ?? '';
  try {
    await ModifyLinkService.submitEventInfo({
      id,
      bundleToken: linkData.bundleToken,
      data: eventStore.clientData,
      patternId: selectedPatternId.value,
      isGallery: isGallery.value ?? true,
    });
  } catch (err) {
    toast.error(
      'Bląd podczas przesyłania formularza. Proszę spróbować ponownie.',
    );
    isError = true;
  }

  isFormSending.value = false;
  if (!isError) {
    isFormSended.value = true;
  }
}

onMounted(async () => {
  await changeSelectedPattern(DEFAULT_PATTERN_ID);
  setGallery();
  setUnderTextData();
  setInitials();
});
</script>

<template>
  <div class="modify-event">
    <h1 class="modify-event-header">Witajcie</h1>
    <div class="modify-event-container">
      <form class="modify-event-form" @submit.prevent="submitEventForm">
        <p class="modify-event-form-subheader">
          Aby stworzyć waszą unikalną aplikację, prosimy wybrać jeden z
          dostępnych wzorów:
        </p>
        <ul class="modify-event-form-list modify-event-form-patterns">
          <li
            v-for="(i, idx) in 3"
            :key="i"
            class="modify-event-form-list-element modify-event-form-patterns-element"
            :class="{
              'modify-event-form-patterns-element-selected':
                idx === selectedPatternId,
            }"
            @click="changeSelectedPattern(idx)"
          >
            <p class="modify-event-form-list-element-p">Wzór {{ i }}</p>
          </li>
        </ul>
        <button
          class="modify-event-form-preview__button"
          type="button"
          @click="moveToPreview"
        >
          Zobacz wygląd aplikacji
        </button>
        <p class="modify-event-form-subheader">
          Oraz odpowiedzieć na poniższe pytania:
        </p>
        <ul class="modify-event-form-list">
          <li class="modify-event-form-list-element">
            <p class="modify-event-form-list-element-p">
              Czy galeria ma być widoczna również dla Waszych gości?
            </p>
            <div class="modify-event-form-checkbox">
              <label class="modify-event-form-checkbox-option">
                <p class="modify-event-form-checkbox-option-label">Tak</p>
                <div class="modify-event-form-checkbox-option-box">
                  <input
                    v-model="normalIsGallery"
                    type="checkbox"
                    class="modify-event-form-checkbox-input"
                    :class="{
                      'modify-event-form-checkbox-input__checked': isGallery,
                    }"
                  />
                  <span class="modify-event-form-checkbox-checkmark"></span>
                </div>
              </label>
              <label class="modify-event-form-checkbox-option">
                <p class="modify-event-form-checkbox-option-label">Nie</p>
                <div class="modify-event-form-checkbox-option-box">
                  <input
                    v-model="reverseIsGallery"
                    type="checkbox"
                    class="modify-event-form-checkbox-input"
                    :class="{
                      'modify-event-form-checkbox-input__checked': !isGallery,
                    }"
                  />
                  <span class="modify-event-form-checkbox-checkmark"></span>
                </div>
              </label>
            </div>
          </li>
          <li v-if="eventDate" class="modify-event-form-list-element">
            <p class="modify-event-form-list-element-p">
              Czy mają być widoczne Wasze inicjały?
            </p>
            <div class="modify-event-form-checkbox">
              <label class="modify-event-form-checkbox-option">
                <p class="modify-event-form-checkbox-option-label">Tak</p>
                <div class="modify-event-form-checkbox-option-box">
                  <input
                    v-model="normalIsInitials"
                    type="checkbox"
                    class="modify-event-form-checkbox-input"
                    :class="{
                      'modify-event-form-checkbox-input__checked': isInitials,
                    }"
                  />
                  <span class="modify-event-form-checkbox-checkmark"></span>
                </div>
              </label>
              <label class="modify-event-form-checkbox-option">
                <p class="modify-event-form-checkbox-option-label">Nie</p>
                <div class="modify-event-form-checkbox-option-box">
                  <input
                    v-model="reverseIsInitials"
                    type="checkbox"
                    class="modify-event-form-checkbox-input"
                    :class="{
                      'modify-event-form-checkbox-input__checked': !isInitials,
                    }"
                  />
                  <span class="modify-event-form-checkbox-checkmark"></span>
                </div>
              </label>
            </div>
          </li>
          <li v-if="eventDate" class="modify-event-form-list-element">
            <p class="modify-event-form-list-element-p">
              Czy ma być widoczna pełna data wesela na dole aplikacji?
            </p>
            <div class="modify-event-form-checkbox">
              <label class="modify-event-form-checkbox-option">
                <p class="modify-event-form-checkbox-option-label">Tak</p>
                <div class="modify-event-form-checkbox-option-box">
                  <input
                    v-model="normalIsFullDate"
                    type="checkbox"
                    class="modify-event-form-checkbox-input"
                    :class="{
                      'modify-event-form-checkbox-input__checked': isFullDate,
                    }"
                  />
                  <span class="modify-event-form-checkbox-checkmark"></span>
                </div>
              </label>
              <label class="modify-event-form-checkbox-option">
                <p class="modify-event-form-checkbox-option-label">Nie</p>
                <div class="modify-event-form-checkbox-option-box">
                  <input
                    v-model="reverseIsFullDate"
                    type="checkbox"
                    class="modify-event-form-checkbox-input"
                    :class="{
                      'modify-event-form-checkbox-input__checked': !isFullDate,
                    }"
                  />
                  <span class="modify-event-form-checkbox-checkmark"></span>
                </div>
              </label>
            </div>
          </li>
          <li v-else class="modify-event-form-list-element">
            <p class="modify-event-form-list-element-p">
              Tekst z datą Waszego wesela
            </p>
            <input
              v-model="eventStore.getLangAllLangData().underTextData"
              type="text"
              class="modify-event-form-text"
            />
          </li>
        </ul>
        <div class="modify-event-form-submit">
          <button class="modify-event-form-submit-button" type="submit">
            Zatwierdź i wyślij
          </button>
        </div>
      </form>
      <div id="preview" class="modify-event-preview">
        <h1 class="modify-event-preview-header">Wygląd Waszej aplikacji</h1>
        <div class="modify-event-preview--wraper">
          <VersionChooser
            class="modify-event-preview-site"
            :version-list="EventVersionList"
            :product-type="ProductType.EVENT"
            :store="useEventStore"
          ></VersionChooser>
        </div>
      </div>
    </div>
  </div>

  <transition name="fade">
    <SendingFormPopUp v-if="isFormSending"></SendingFormPopUp>
    <SendedFormPopUp v-else-if="isFormSended"></SendedFormPopUp>
  </transition>
</template>

<style lang="scss" scoped>
.modify-event {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background: $white-100;
  color: $black;
  font-size: 16px;
  padding-bottom: 110px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-top: 50px;
  align-items: center;

  &-header {
    font-size: 3.5em;
    line-height: 1.5em;
    font-weight: 500;
    color: $brown-200;

    @media screen and (max-width: $media-max-phone) {
      font-size: 2.5em;
      line-height: 1em;
    }
  }

  &-container {
    display: flex;
    flex-flow: row wrap;
    gap: 25px;
    width: 90%;
    margin: 0 auto;
  }

  &-form {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    flex-direction: column;
    flex: 1 1 45%;

    &-subheader {
      font-size: 1.75em;
      line-height: 1.5em;
      color: $brown-200;
      text-align: center;
      font-weight: 700;

      @media screen and (max-width: $media-max-phone) {
        font-size: 1.25em;
        line-height: 1.25em;
      }
    }

    &-p {
      font-size: 1em;
      text-align: center;
    }

    &-text,
    &-date {
      border: 1px solid $black;
      border-radius: 15px;
      padding: 5px 10px;
      flex: 1 1 auto;
      background-color: $white-100;
      width: 100%;
      max-width: 350px;
    }

    &-checkbox {
      display: flex;
      flex-direction: row;
      width: 100%;
      justify-content: space-evenly;
      gap: 35px;
      max-width: 550px;

      &-option {
        display: flex;
        flex-direction: column;
        gap: 8px;
        position: relative;
        margin-bottom: 25px;
        cursor: pointer;
        font-size: 22px;
        user-select: none;

        &-label {
          color: $brown-200;
          font-size: 20px;
          font-weight: 500;
        }

        &-box {
          position: relative;
          display: block;
        }
      }

      &-input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
      }

      &-checkmark {
        position: absolute;
        top: 0;
        left: calc(50% - 12px);
        height: 25px;
        width: 25px;
        background-color: $white-100;
        border: 2px solid $black;
        border-radius: 5px;

        &::after {
          content: '';
          position: absolute;
          display: none;
          left: 9px;
          top: 5px;
          width: 5px;
          height: 10px;
          border: solid $white-100;
          border-width: 0 3px 3px 0;
          transform: rotate(45deg);
        }
      }

      &-input__checked ~ &-checkmark::after {
        display: block;
      }

      &-input__checked ~ &-checkmark {
        background-color: $brown-200;
      }

      &-option:hover &-input ~ &-checkmark {
        border-color: $brown-200;
      }
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: 15px;
      list-style-type: none;
      width: 100%;

      &-element {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        gap: 15px;

        &-p {
          font-size: 1.3em;
          font-weight: 600;
          color: $brown-200;
          text-align: center;

          @media screen and (max-width: $media-max-phone) {
            font-size: 1.2em;
          }
        }
      }
    }

    &-patterns {
      flex-direction: column;

      @media screen and (max-width: $media-wide-desktop) {
        flex-flow: row;
      }

      &-element {
        margin: 0 auto;
        width: 50%;
        border: 1px solid $brown-200;
        border-radius: 15px;
        padding: 6px;
        cursor: pointer;

        &:hover {
          border: 2px solid $brown-200;
          padding: 5px;
        }

        &-selected {
          background-color: $brown-200;
          border: 2px solid $brown-200;
          padding: 5px;

          .modify-event-form-list-element-p {
            color: $white-100;
          }
        }
      }
    }

    &-submit {
      position: fixed;
      bottom: 0;
      right: 0;
      z-index: 99;
      background-color: $white-100;
      width: 100%;
      display: flex;
      justify-content: center;
      padding: 25px 0;

      &-button {
        font-size: 1.5em;
        font-weight: 700;
        border-radius: 14px;
        border: 2px solid $brown-200;
        padding: 10px 25px;
        cursor: pointer;
        transition: ease 0.7s all;
        color: $brown-200;

        &:hover {
          background-color: $brown-200;
          color: $white-100;
        }
      }
    }

    &-preview__button {
      display: none;
      font-size: 20px;
      font-weight: 700;
      border-radius: 14px;
      border: 2px solid $brown-200;
      padding: 10px 25px;
      cursor: pointer;
      transition: ease 0.7s all;
      color: $brown-200;

      &:hover {
        background-color: $brown-200;
        color: $white-100;
      }

      @media screen and (max-width: $media-max-tablet) {
        display: block;
      }
    }
  }

  &-preview {
    height: 100%;
    flex: 1 1 40%;
    display: flex;
    flex-direction: column;
    gap: 15px;
    min-width: 450px;

    &-header {
      font-size: 2em;
      line-height: 1.5em;
      font-weight: 600;
      color: $brown-200;
      text-align: center;
      width: 100%;
      padding: 0 10px;

      @media screen and (max-width: $media-max-phone) {
        font-size: 2em;
      }
    }

    &--wraper {
      height: 70vh;
      width: 100%;
      overflow: hidden;
    }

    &-site {
      border-radius: 10px;
      overflow-y: scroll;
      height: 200%;
      width: 200%;
      transform: scale(0.5);
      transform-origin: top left;

      @media screen and (max-width: $media-max-phone) {
        height: 100%;
        width: 100%;
        transform: scale(1);
      }
    }
  }
}

.fade-enter-active {
  transition: opacity 1s ease;
}

.fade-enter-from {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from,
.fade-leave-to {
  opacity: 0.8;
}
</style>
